import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { REQUST_TIMEOUT } from '../util/environment';

import { getAccessToken, refreshAccessToken } from './auth-session';
import { StatusCode } from '../enum/StatusCode';

type CreateAxiosInstanceOptions = {
  baseURL: string;

  shouldRefresh?: (response: AxiosResponse) => boolean;
};

export const createAxiosInstance = ({ baseURL, shouldRefresh }: CreateAxiosInstanceOptions): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: REQUST_TIMEOUT,
  });

  axiosInstance.interceptors.request.use(
    async (config) => {
      if (config && config.headers) {
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    async (response: AxiosResponse) => {
      const originalRequest = response.config as
        | (AxiosRequestConfig & {
            _retry?: boolean;
          })
        | undefined;

      if (shouldRefresh?.(response) && originalRequest && !originalRequest._retry) {
        return refreshAndRetry(axiosInstance, originalRequest);
      }

      return response;
    },

    async (error: AxiosError) => {
      const originalRequest = error.config as
        | (AxiosRequestConfig & {
            _retry?: boolean;
          })
        | undefined;

      const status = error.response?.status;

      if (status !== StatusCode.UNAUTHORIZED || !originalRequest || originalRequest._retry) {
        return Promise.reject(error);
      }

      return refreshAndRetry(axiosInstance, originalRequest);
    },
  );

  return axiosInstance;
};

const refreshAndRetry = async (
  axiosInstance: AxiosInstance,
  originalRequest: AxiosRequestConfig & {
    _retry?: boolean;
  },
): Promise<AxiosResponse> => {
  originalRequest._retry = true;

  const newToken = await refreshAccessToken();

  if (originalRequest.headers) {
    (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
  }

  return axiosInstance.request(originalRequest);
};
